import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

function clean(value) {
  return String(value ?? "").trim();
}

function safeImageUrl(value) {
  const candidate = clean(value);

  if (!candidate) return "";

  try {
    const parsed = new URL(candidate);

    return (
      parsed.protocol === "http:" ||
      parsed.protocol === "https:"
    )
      ? parsed.toString()
      : "";
  } catch {
    return "";
  }
}

function activeAssetForUnit(
  assets,
  unitId
) {
  return (
    Array.isArray(assets)
      ? assets
      : []
  ).find(
    (asset) =>
      asset?.assetType ===
        "illustration" &&
      asset?.purpose ===
        "book_map_illustration" &&
      asset?.status ===
        "active" &&
      clean(asset?.bookMapUnitId) ===
        clean(unitId)
  ) || null;
}

export default function ChildrensBookIllustrationAssetPanel({
  lang = "en",
  unit,
  compact = false,
  assetManagementEnabled = false,
  managementControlVisible = true,
  childrensBookAssets = [],
  childrensBookAssetsLoading = false,
  childrensBookAssetsError = "",
  childrensBookAssetActionId = "",
  onLoadChildrensBookAssetFile,
  onUploadChildrensBookIllustration,
  onRemoveChildrensBookAsset,
}) {
  const isFR =
    String(lang).toLowerCase() ===
    "fr";

  const unitId =
    clean(unit?.id);

  const unitTitle =
    clean(unit?.workingTitle) ||
    (
      isFR
        ? "Unité du Plan du livre"
        : "Book Map unit"
    );

  const activeIllustration =
    useMemo(
      () =>
        activeAssetForUnit(
          childrensBookAssets,
          unitId
        ),
      [
        childrensBookAssets,
        unitId,
      ]
    );

  const [
    managing,
    setManaging,
  ] = useState(false);

  useEffect(() => {
    if (
      !managementControlVisible &&
      managing
    ) {
      setManaging(false);
    }
  }, [
    managementControlVisible,
    managing,
  ]);

  const [
    selectedFile,
    setSelectedFile,
  ] = useState(null);

  const [
    selectedPreviewUrl,
    setSelectedPreviewUrl,
  ] = useState("");

  const [
    persistedPreviewUrl,
    setPersistedPreviewUrl,
  ] = useState("");

  const [
    previewLoading,
    setPreviewLoading,
  ] = useState(false);

  const [
    previewError,
    setPreviewError,
  ] = useState("");

  const [
    rightsConfirmed,
    setRightsConfirmed,
  ] = useState(false);

  const [
    submitting,
    setSubmitting,
  ] = useState(false);

  const [
    actionMessage,
    setActionMessage,
  ] = useState("");

  const [
    actionError,
    setActionError,
  ] = useState("");

  const [
    pendingRemoval,
    setPendingRemoval,
  ] = useState(false);

  const protectedPreview =
    Boolean(
      activeIllustration &&
      (
        activeIllustration.source ===
          "upload" ||
        activeIllustration
          .storageProvider ===
          "local"
      )
    );

  const actionId =
    clean(
      childrensBookAssetActionId
    );

  const unitUploadActionId =
    `upload-illustration-${unitId}`;

  const actionMatchesThisUnit =
    actionId ===
      unitUploadActionId ||
    (
      Boolean(activeIllustration) &&
      actionId ===
        clean(activeIllustration?.id)
    );

  const assetBusy =
    submitting ||
    actionMatchesThisUnit;

  const uploadBusy =
    (
      submitting &&
      Boolean(selectedFile)
    ) ||
    actionId ===
      unitUploadActionId;

  useEffect(() => {
    return () => {
      if (
        selectedPreviewUrl &&
        typeof URL !== "undefined" &&
        typeof URL.revokeObjectURL ===
          "function"
      ) {
        URL.revokeObjectURL(
          selectedPreviewUrl
        );
      }
    };
  }, [selectedPreviewUrl]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";

    const publicUrl =
      clean(
        activeIllustration?.publicUrl
      );

    setPersistedPreviewUrl("");
    setPreviewError("");

    if (
      !protectedPreview ||
      !publicUrl ||
      typeof onLoadChildrensBookAssetFile !==
        "function"
    ) {
      setPreviewLoading(false);
      return () => {};
    }

    setPreviewLoading(true);

    async function loadPreview() {
      try {
        const blob =
          await onLoadChildrensBookAssetFile(
            publicUrl
          );

        if (cancelled) return;

        if (
          typeof URL === "undefined" ||
          typeof URL.createObjectURL !==
            "function"
        ) {
          throw new Error(
            isFR
              ? "Le navigateur n’a pas pu préparer l’aperçu."
              : "The browser could not prepare the preview."
          );
        }

        objectUrl =
          URL.createObjectURL(blob);

        if (!cancelled) {
          setPersistedPreviewUrl(
            objectUrl
          );
        }
      } catch (error) {
        if (!cancelled) {
          setPreviewError(
            clean(error?.message) ||
            (
              isFR
                ? "Impossible de charger l’illustration enregistrée."
                : "Could not load the saved illustration."
            )
          );
        }
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    }

    loadPreview();

    return () => {
      cancelled = true;

      if (
        objectUrl &&
        typeof URL !== "undefined" &&
        typeof URL.revokeObjectURL ===
          "function"
      ) {
        URL.revokeObjectURL(
          objectUrl
        );
      }
    };
  }, [
    activeIllustration?.publicUrl,
    protectedPreview,
    isFR,
    onLoadChildrensBookAssetFile,
  ]);

  const previewUrl =
    selectedPreviewUrl ||
    persistedPreviewUrl ||
    (
      protectedPreview
        ? ""
        : safeImageUrl(
            activeIllustration
              ?.publicUrl
          )
    );

  function selectFile(event) {
    const file =
      event.target.files?.[0] ||
      null;

    setActionMessage("");
    setActionError("");

    if (!file) {
      setSelectedFile(null);
      setSelectedPreviewUrl("");
      return;
    }

    const allowedTypes =
      new Set([
        "image/jpeg",
        "image/png",
        "image/webp",
      ]);

    if (
      !allowedTypes.has(
        clean(file.type)
          .toLowerCase()
      )
    ) {
      event.target.value = "";

      setActionError(
        isFR
          ? "Choisissez une image JPG, PNG ou WEBP."
          : "Choose a JPG, PNG, or WEBP image."
      );

      return;
    }

    if (
      Number(file.size || 0) >
      5 * 1024 * 1024
    ) {
      event.target.value = "";

      setActionError(
        isFR
          ? "L’illustration ne doit pas dépasser 5 Mo."
          : "The illustration must not exceed 5 MB."
      );

      return;
    }

    setSelectedFile(file);

    if (
      typeof URL !== "undefined" &&
      typeof URL.createObjectURL ===
        "function"
    ) {
      setSelectedPreviewUrl(
        URL.createObjectURL(file)
      );
    }
  }

  async function uploadIllustration() {
    setActionMessage("");
    setActionError("");

    if (!assetManagementEnabled) {
      setActionError(
        isFR
          ? "Enregistrez d’abord le projet."
          : "Save the project first."
      );
      return;
    }

    if (!selectedFile) {
      setActionError(
        isFR
          ? "Choisissez d’abord une illustration."
          : "Choose an illustration first."
      );
      return;
    }

    if (!rightsConfirmed) {
      setActionError(
        isFR
          ? "Confirmez que vous détenez les droits ou l’autorisation d’utiliser cette illustration."
          : "Confirm that you own or have permission to use this illustration."
      );
      return;
    }

    if (
      typeof onUploadChildrensBookIllustration !==
        "function"
    ) {
      setActionError(
        isFR
          ? "Le téléversement n’est pas disponible."
          : "Illustration upload is unavailable."
      );
      return;
    }

    try {
      setSubmitting(true);

      await onUploadChildrensBookIllustration(
        unitId,
        selectedFile
      );

      setActionMessage(
        activeIllustration
          ? (
              isFR
                ? "L’illustration a été remplacée."
                : "The illustration has been replaced."
            )
          : (
              isFR
                ? "L’illustration a été ajoutée."
                : "The illustration has been added."
            )
      );

      setSelectedFile(null);
      setSelectedPreviewUrl("");
      setRightsConfirmed(false);
    } catch (error) {
      setActionError(
        clean(error?.message) ||
        (
          isFR
            ? "Impossible de téléverser cette illustration."
            : "Could not upload this illustration."
        )
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function confirmRemoval(
    event
  ) {
    event.preventDefault();

    if (
      !activeIllustration ||
      typeof onRemoveChildrensBookAsset !==
        "function"
    ) {
      setPendingRemoval(false);
      return;
    }

    try {
      setSubmitting(true);
      setActionMessage("");
      setActionError("");

      await onRemoveChildrensBookAsset(
        activeIllustration.id,
        activeIllustration.revision
      );

      setActionMessage(
        isFR
          ? "L’illustration a été retirée sans supprimer le fichier source."
          : "The illustration was removed without deleting the source file."
      );

      setPersistedPreviewUrl("");
      setPendingRemoval(false);
      setRightsConfirmed(false);
    } catch (error) {
      setActionError(
        clean(error?.message) ||
        (
          isFR
            ? "Impossible de retirer cette illustration."
            : "Could not remove this illustration."
        )
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (compact) {
    if (!activeIllustration) {
      return null;
    }

    return (
      <div
        data-testid={
          `childrens-book-illustration-thumbnail-${unitId}`
        }
        title={
          isFR
            ? "Illustration jointe"
            : "Illustration attached"
        }
        style={{
          marginTop: 10,
          width: 88,
          height: 66,
          padding: 4,
          borderRadius: 10,
          border:
            "1px solid rgba(22,101,52,0.24)",
          background: "#ffffff",
          overflow: "hidden",
          display: "grid",
          placeItems: "center",
          boxSizing: "border-box",
        }}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={
              isFR
                ? `Miniature de l’illustration jointe — ${unitTitle}`
                : `Attached illustration thumbnail — ${unitTitle}`
            }
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "contain",
              borderRadius: 6,
            }}
          />
        ) : (
          <span
            aria-label={
              isFR
                ? "Illustration jointe"
                : "Illustration attached"
            }
            style={{
              fontSize: 10,
              fontWeight: 800,
              color: "#166534",
              textAlign: "center",
              lineHeight: 1.25,
              padding: 4,
            }}
          >
            {previewLoading
              ? (
                  isFR
                    ? "Chargement…"
                    : "Loading…"
                )
              : (
                  isFR
                    ? "Illustration"
                    : "Illustration"
                )}
          </span>
        )}
      </div>
    );
  }

  return (
    <section
      aria-label={
        isFR
          ? `Fichier d’illustration — ${unitTitle}`
          : `Illustration file — ${unitTitle}`
      }
      style={{
        marginTop: 16,
        padding: 14,
        borderRadius: 14,
        border:
          activeIllustration
            ? "1px solid rgba(22,101,52,0.28)"
            : "1px solid rgba(37,99,235,0.22)",
        background:
          activeIllustration
            ? "#f0fdf4"
            : "#f8fbff",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <strong>
            {activeIllustration
              ? (
                  isFR
                    ? "Illustration jointe"
                    : "Illustration attached"
                )
              : (
                  isFR
                    ? "Aucun fichier d’illustration"
                    : "No illustration file"
                )}
          </strong>

          <div
            className="ns-card-meta"
            style={{ marginTop: 4 }}
          >
            {activeIllustration
              ? (
                  activeIllustration
                    .fileName ||
                  (
                    isFR
                      ? "Fichier enregistré"
                      : "Saved file"
                  )
                )
              : (
                  isFR
                    ? "Le fichier reste distinct des directives."
                    : "The file remains separate from the directions."
                )}
          </div>
        </div>

        {!managing &&
        activeIllustration &&
        previewUrl ? (
          <div
            style={{
              display: "grid",
              placeItems: "center",
              width: 112,
              height: 82,
              padding: 6,
              borderRadius: 10,
              border:
                "1px solid rgba(22,101,52,0.22)",
              background: "#ffffff",
              overflow: "hidden",
            }}
          >
            <img
              src={previewUrl}
              alt={
                isFR
                  ? `Miniature de l’illustration jointe — ${unitTitle}`
                  : `Attached illustration thumbnail — ${unitTitle}`
              }
              style={{
                display: "block",
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: 6,
              }}
            />
          </div>
        ) : null}

        {managementControlVisible ? (
          <button
            className="ns-button ns-button-outline"
            type="button"
            onClick={() =>
              setManaging(
                (current) => !current
              )
            }
          >
            {managing
              ? (
                  isFR
                    ? "Fermer"
                    : "Close"
                )
              : (
                  isFR
                    ? "Gérer l’image"
                    : "Manage Image"
                )}
          </button>
        ) : null}
      </div>

      {managing ? (
        <div
          style={{
            display: "grid",
            gap: 14,
            marginTop: 16,
          }}
        >
          {childrensBookAssetsLoading ? (
            <div
              role="status"
              className="ns-card-meta"
            >
              {isFR
                ? "Chargement…"
                : "Loading…"}
            </div>
          ) : null}

          {previewLoading ? (
            <div
              role="status"
              className="ns-card-meta"
            >
              {isFR
                ? "Chargement de l’aperçu…"
                : "Loading preview…"}
            </div>
          ) : null}

          {previewError ? (
            <div
              role="alert"
              style={{
                color: "#b91c1c",
                fontWeight: 700,
              }}
            >
              {previewError}
            </div>
          ) : null}

          {previewUrl ? (
            <div
              style={{
                display: "grid",
                placeItems: "center",
                padding: 14,
                borderRadius: 12,
                background: "#ffffff",
                border:
                  "1px solid rgba(148,163,184,0.35)",
              }}
            >
              <img
                src={previewUrl}
                alt={
                  isFR
                    ? `Aperçu de l’illustration — ${unitTitle}`
                    : `Illustration preview — ${unitTitle}`
                }
                style={{
                  display: "block",
                  width: "100%",
                  maxWidth: 520,
                  maxHeight: 420,
                  objectFit: "contain",
                  borderRadius: 10,
                }}
              />
            </div>
          ) : null}

          <label
            style={{
              display: "grid",
              gap: 7,
              fontWeight: 800,
            }}
          >
            <span>
              {isFR
                ? "Fichier d’illustration"
                : "Illustration image file"}
            </span>

            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              disabled={
                !assetManagementEnabled ||
                assetBusy
              }
              onChange={selectFile}
            />

            <span className="ns-card-meta">
              {isFR
                ? "JPG, PNG ou WEBP. Maximum : 5 Mo."
                : "JPG, PNG, or WEBP. Maximum: 5 MB."}
            </span>

            {selectedFile ? (
              <span
                style={{
                  color: "#166534",
                  fontWeight: 800,
                }}
              >
                {selectedFile.name}
              </span>
            ) : null}
          </label>

          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              lineHeight: 1.5,
            }}
          >
            <input
              type="checkbox"
              checked={rightsConfirmed}
              onChange={(event) =>
                setRightsConfirmed(
                  event.target.checked
                )
              }
            />

            <span>
              {isFR
                ? "Je confirme que je possède cette illustration ou que j’ai l’autorisation de l’utiliser."
                : "I confirm that I own this illustration or have permission to use it."}
            </span>
          </label>

          {!assetManagementEnabled ? (
            <div
              className="ns-card-meta"
              style={{
                color: "#92400e",
                fontWeight: 700,
              }}
            >
              {isFR
                ? "Enregistrez d’abord le Blueprint."
                : "Save the Blueprint first."}
            </div>
          ) : null}

          {childrensBookAssetsError ? (
            <div
              role="alert"
              style={{
                color: "#b91c1c",
                fontWeight: 700,
              }}
            >
              {childrensBookAssetsError}
            </div>
          ) : null}

          {actionError ? (
            <div
              role="alert"
              style={{
                color: "#b91c1c",
                fontWeight: 700,
              }}
            >
              {actionError}
            </div>
          ) : null}

          {actionMessage ? (
            <div
              role="status"
              aria-live="polite"
              style={{
                color: "#166534",
                fontWeight: 700,
              }}
            >
              {actionMessage}
            </div>
          ) : null}

          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <button
              className="ns-button ns-button-primary"
              type="button"
              disabled={
                assetBusy ||
                !assetManagementEnabled ||
                !selectedFile
              }
              aria-busy={uploadBusy}
              onClick={uploadIllustration}
            >
              {uploadBusy
                ? (
                    isFR
                      ? "Enregistrement…"
                      : "Saving…"
                  )
                : activeIllustration
                  ? (
                      isFR
                        ? "Téléverser et remplacer"
                        : "Upload and Replace"
                    )
                  : (
                      isFR
                        ? "Téléverser cette illustration"
                        : "Upload This Illustration"
                    )}
            </button>

            {activeIllustration ? (
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={assetBusy}
                onClick={() =>
                  setPendingRemoval(true)
                }
                style={{
                  borderColor: "#dc2626",
                  color: "#b91c1c",
                }}
              >
                {isFR
                  ? "Retirer de cette unité"
                  : "Remove from This Unit"}
              </button>
            ) : null}
          </div>

          <div className="ns-card-meta">
            {isFR
              ? "Le remplacement et le retrait conservent les anciens enregistrements."
              : "Replacement and removal preserve earlier records."}
          </div>
        </div>
      ) : null}

      {pendingRemoval ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            display: "grid",
            placeItems: "center",
            padding: 20,
            background:
              "rgba(15,23,42,0.58)",
          }}
        >
          <form
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={
              `remove-illustration-${unitId}`
            }
            onSubmit={confirmRemoval}
            className="ns-card"
            style={{
              width:
                "min(540px, 100%)",
              padding: 24,
              borderRadius: 18,
            }}
          >
            <h3
              id={
                `remove-illustration-${unitId}`
              }
              className="ns-card-title"
              style={{ marginTop: 0 }}
            >
              {isFR
                ? "Retirer cette illustration ?"
                : "Remove This Illustration?"}
            </h3>

            <p className="ns-card-meta">
              {isFR
                ? `L’illustration sera retirée de « ${unitTitle} ». Le fichier source et les anciens enregistrements ne seront pas supprimés.`
                : `The illustration will be removed from “${unitTitle}.” The source file and earlier records will not be deleted.`}
            </p>

            <div
              style={{
                display: "flex",
                justifyContent:
                  "flex-end",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={assetBusy}
                onClick={() =>
                  setPendingRemoval(false)
                }
              >
                {isFR
                  ? "Annuler"
                  : "Cancel"}
              </button>

              <button
                className="ns-button"
                type="submit"
                disabled={assetBusy}
                style={{
                  background: "#b91c1c",
                  borderColor: "#b91c1c",
                  color: "#ffffff",
                }}
              >
                {isFR
                  ? "Retirer l’illustration"
                  : "Remove Illustration"}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </section>
  );
}
